import type { ServerProduct } from '@/lib/janitsjar/Product';
import type { SVGProps } from 'react';
import { EyeIcon, TruckIcon } from '@fagmobler/ui';
import { useStores } from '@/components/store';
import { OmniumStore } from '@fagmobler/omnium/src/types';

export function getProductIndicators(
  currentStore: OmniumStore,
  product: ServerProduct
) {
  const currentStoreStock =
    product.stock?.find((s) => s.storeId === currentStore?.id) || null;

  const displayUnits = currentStoreStock?.display ?? 0;
  const availableToOrder = currentStoreStock?.availableToOrder ?? false;
  const deliveryEstimate = currentStoreStock?.stockText ?? '';

  const hasDisplayUnits = !!(currentStore?.name && displayUnits > 0);
  const hasDeliveryEstimate = !!(
    deliveryEstimate &&
    availableToOrder &&
    !currentStoreStock?.stock
  );

  return {
    deliveryEstimate,
    hasDisplayUnits,
    hasDeliveryEstimate,
    hasIndicators: hasDisplayUnits || hasDeliveryEstimate,
  };
}

type ProductIndicatorRowProps = {
  Icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  text: string;
};

function ProductIndicatorRow({ Icon, text }: ProductIndicatorRowProps) {
  return (
    <div className="flex gap-2 items-center">
      <Icon className="w-4 h-4" />
      <span className="body-sm">{text}</span>
    </div>
  );
}

type ProductIndicatorsProps = {
  product: ServerProduct;
};

export default function ProductIndicators({ product }: ProductIndicatorsProps) {
  const { current: currentStoreQuery } = useStores();

  const currentStore = currentStoreQuery.data?.store;

  if (!currentStore) return null;

  const {
    hasDisplayUnits,
    hasDeliveryEstimate,
    deliveryEstimate,
    hasIndicators,
  } = getProductIndicators(currentStore, product);

  if (!hasIndicators) return null;

  return (
    <div className="flex flex-col gap-1">
      {hasDisplayUnits && (
        <ProductIndicatorRow
          Icon={EyeIcon}
          text={`Utstilt i ${currentStore?.name?.replace('Fagmøbler', '')}`}
        />
      )}
      {hasDeliveryEstimate && (
        <ProductIndicatorRow
          Icon={TruckIcon}
          text={`Kan leveres hjem (${deliveryEstimate})`}
        />
      )}
    </div>
  );
}
